<template>
  <div class="form__wrapper">
    <form v-if="!ui.submitted && !ui.submitError"
          class="form"
          @submit.prevent="submitForm">
      <div class="form__row">
        <div class="form__column">
          <div class="form-group">
            <div class="d-flex justify-content-between">
              <label for="firstName">Your first name:
                <span class="asterix">*</span>
              </label>
              <div v-if="!$v.firstName.required && ui.submitAttempted"
                   class="validation-error">
                Please enter your first name
              </div>
            </div>
            <input
              id="firstName"
              v-model.trim="firstName"
              name="firstName"
              class="form-control"
              :class="{ form__error: !$v.firstName.required && ui.submitAttempted }"
              :placeholder="firstNamePlaceholder">
          </div>

          <div class="form-group">
            <div class="d-flex justify-content-between">
              <label for="lastName">Your {{ isInternational ? "family" : "last" }} name:
                <span class="asterix">*</span>
              </label>
              <div v-if="!$v.lastName.required && ui.submitAttempted"
                   class="validation-error">
                Please enter your {{ isInternational ? "family" : "last" }} name
              </div>
            </div>
            <input
              id="lastName"
              v-model.trim="lastName"
              name="lastName"
              class="form-control"
              :class="{ form__error: !$v.lastName.required && ui.submitAttempted }"
              :placeholder="lastNamePlaceholder">
          </div>

          <div class="form-group">
            <div class="d-flex justify-content-between">
              <label for="organisation">School or organisation name:
                <span class="asterix">*</span>
              </label>
              <div v-if="!$v.organisation.required && ui.submitAttempted"
                   class="validation-error">
                Please enter your organisation
              </div>
            </div>

            <input
              id="organisation"
              v-model.trim="organisation"
              name="organisation"
              class="form-control"
              :class="{ form__error: !$v.organisation.required && ui.submitAttempted }"
              :placeholder="organisationPlaceholder">
          </div>

          <div class="form-group">
            <div class="d-flex justify-content-between">
              <label for="email">Email Address:
                <span class="asterix">*</span>
              </label>
              <div v-if="!$v.email.required && ui.submitAttempted"
                   class="validation-error">
                Please enter your email address
              </div>
            </div>
            <input
              id="email"
              v-model.trim="email"
              name="email"
              type="email"
              class="form-control"
              :class="{ form__error: !$v.email.required && ui.submitAttempted }"
              :placeholder="emailPlaceholder">
          </div>

          <div class="form-group">
            <label for="telephone">Telephone Number:
              <!-- <span class="asterix">*</span> -->
            </label>
            <input
              id="telephone"
              v-model.trim="telephone"
              name="telephone"
              type="tel"
              class="form-control"
              :placeholder="telephonePlaceholder">
          </div>
        </div>
        <div class="form__column">
          <div class="form-group">
            <div class="d-flex justify-content-between">
              <label for="message">Your message:
                <span class="asterix">*</span>
              </label>
              <div v-if="!$v.message.required && ui.submitAttempted"
                   class="validation-error">
                Please enter a message
              </div>
            </div>
            <textarea
              id="message"
              v-model="message"
              name="message"
              class="form-control message-input form__textArea"
              :class="{ form__error: !$v.message.required && ui.submitAttempted }"
              :placeholder="messagePlaceholder"
              rows="16"></textarea>
          </div>
          <div class="form-group text-right">
            <button class="btn btn-accent-2"
                    type="submit">
              <template v-if="!ui.submitting">
                <span>{{ submitButtonLabel }}</span>
                <font-awesome-icon icon="chevron-right"
                                   aria-hidden="true">
                </font-awesome-icon>
              </template>
              <template v-else>
                <font-awesome-icon icon="spinner"
                                   aria-hidden="true"
                                   spin></font-awesome-icon>
              </template>
            </button>
          </div>
        </div>
      </div>
    </form>
    <div v-if="ui.submitted && !ui.submitError"
         class="form-feedback">
      <h3 class="submit-success">
        Thank you!
      </h3>
      <p>Your message has been received.</p>
    </div>
    <div v-if="ui.submitError"
         class="form-feedback">
      <h3 class="submit-error">
        Oh no!
      </h3>
      <p>Something went wrong whilst sending your message.</p>
      <p v-if="errorEmailAddress">
        Please email us at
        <a :href="`mailto:${errorEmailAddress}`">{{ errorEmailAddress }}</a>.
      </p>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import { required, email } from "vuelidate/lib/validators";
import axios from "axios";
import qs from "qs";
import { VueReCaptcha } from "vue-recaptcha-v3";

// For more options see below
const siteKey = document.querySelector( "meta[name=\"siteKey\"]" ).getAttribute( "content" );
Vue.use( VueReCaptcha, { siteKey } );

export default {
  name: "ContactForm",
  props: {
    firstNamePlaceholder: {
      type: String,
      default: "First Name",
    },
    lastNamePlaceholder: {
      type: String,
      default: "Last Name",
    },
    emailPlaceholder: {
      type: String,
      default: "Email",
    },
    telephonePlaceholder: {
      type: String,
      default: "Telephone",
    },
    organisationPlaceholder: {
      type: String,
      default: "School Name",
    },
    messagePlaceholder: {
      type: String,
      default: "",
    },
    submitButtonLabel: {
      type: String,
      default: "Submit message",
    },
    errorEmailAddress: {
      type: String,
      default: undefined,
    },
    isInternational: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      firstName: null,
      lastName: null,
      email: null,
      telephone: null,
      organisation: null,
      message: null,
      ui: {
        submitAttempted: false,
        submitError: false,
        submitting: false,
        submitted: false,
      },
    };
  },
  methods: {
    async getRecaptchaToken () {
      // (optional) Wait until recaptcha has been loaded.
      await this.$recaptchaLoaded();

      return this.$recaptcha( "submit" );
    },
    async submitForm () {
      this.ui.submitAttempted = true;
      const token = await this.getRecaptchaToken();

      if ( !this.$v.$invalid ) {
        this.ui.submitting = true;

        const url = "/umbraco/Surface/ContactSurface/Submit";
        const formData = {
          __RequestVerificationToken: this.$csrfToken,
          "g-recaptcha-response": token,
          firstName: this.firstName,
          lastName: this.lastName,
          emailAddress: this.email,
          telephone: this.telephone,
          orgName: this.organisation,
          message: this.message,
        };

        axios
          .post( url, qs.stringify( formData ) )
          .then( response => {
            if ( response.data === true ) {
              this.firstName = "";
              this.lastName = "";
              this.email = "";
              this.telephone = "";
              this.organisation = "";
              this.message = "";

              this.ui.submitAttempted = false;
              this.ui.submitted = true;
              this.ui.submitting = false;
            } else {
              throw Error( "Bad response" );
            }
          } )
          .catch( error => {
            // Catch it so we can show a pretty error,
            // then throw it again for Sentry to collect.
            this.ui.submitError = true;
            throw new Error( error );
          } );
      }
    },
  },
  validations: {
    firstName: {
      required,
    },
    lastName: {
      required,
    },
    organisation: {
      required,
    },
    email: {
      required,
      email,
    },
    message: {
      required,
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../Styles/utils/index.less";
@crimson: #d61921;

.asterix {
  color: @crimson;
}

.validation-error {
  color: @crimson;
}

.form {
  &__error {
    border: 1px solid @crimson;
  }

  &__wrapper {
    width: 100%;
    padding: 32px 0;
  }

  &__row {
    display: flex;
    flex-wrap: wrap;
  }

  &__column {
    width: 48%;
    min-width: 300px;

    margin: 0 16px 0 0;

    @media (max-width: @lg) {
      margin: 0 0 16px 0;
      width: 100%;
    }
  }

  &__textArea {
    height: 382px;
  }

  &__submit {
    background-color: @crimson;
    min-width: 180px;
    height: 45px;
    color: #fff;
    padding: 8px;
    font-weight: 500;
    border: 1px solid @crimson;
    line-height: 1rem;

    &:focus {
      outline: none;
    }

    &:hover {
      background-color: #fff;
      color: @crimson;
      border: 1px solid @crimson;
    }
  }
}
</style>
